/**
 * Argument maps from an indented outline.
 *
 * The first line is the main claim. Lines indented under a claim are reasons for it
 * (`+` or `Because:`) or objections to it (`-` or `But:`), and the same goes one level
 * down: a reason can have reasons and objections of its own, and an objection to an
 * objection is a rebuttal. A line with no marker is read as a reason. Each box points
 * at the box it bears on, so a map reads from the bottom up, evidence toward the claim.
 *
 * Layout is the plain tree, top-down or left-to-right: every subtree gets a span as wide
 * as its children need, a parent sits over the middle of its children, and each level is
 * one row (or column). Arrows are straight lines from a child to its own point on the
 * parent's edge — points in the same order as the children — so arrows never cross and
 * never leave the gap between two levels.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import { stripBullet } from "@/lib/tool-kit/diagrams/mind-map";
import { indentOf } from "@/lib/tool-kit/diagrams-more/org-chart";

/* ------------------------------------------------------------------- parse */

export type ArgumentKind = "claim" | "reason" | "objection" | "rebuttal";
export type Stance = "support" | "oppose";

export interface ArgumentNode {
  id: number;
  kind: ArgumentKind;
  /** How the box bears on its parent; null for a main claim. */
  stance: Stance | null;
  text: string;
  parent: number | null;
  children: number[];
  depth: number;
  line: number;
  /** `1`, `1.2`, `1.2.1` — a way to refer to the box in an essay. */
  number: string;
}

export interface ArgumentParse {
  nodes: ArgumentNode[];
  roots: number[];
  dropped: number;
  /** Lines with no marker, read as reasons. */
  unmarked: number;
}

export const ARGUMENT_LIMIT = 60;

const LINE_BREAK = /\r\n|\r|\n/;
// The marker words, English first and then the equivalents in the other tool languages
// (es, de, fr, it, pt, nl, pl, ru, tr, id, vi, hi, ja, ko, zh, ar). A word marker needs a
// colon, ASCII or full-width, so ordinary prose that starts with "ma" or "ale" is safe.
const COLON = "\\s*[:：]";
const SUPPORT_WORDS = [
  "because",
  "reason",
  "pro",
  "support",
  "for",
  "porque",
  "razón",
  "a favor",
  "weil",
  "grund",
  "parce que",
  "raison",
  "perché",
  "motivo",
  "razão",
  "omdat",
  "reden",
  "ponieważ",
  "powód",
  "потому что",
  "причина",
  "çünkü",
  "neden",
  "karena",
  "alasan",
  "vì",
  "lý do",
  "क्योंकि",
  "कारण",
  "なぜなら",
  "理由",
  "왜냐하면",
  "이유",
  "因为",
  "因為",
  "لأن",
  "سبب",
].join("|");
const OPPOSE_WORDS = [
  "but",
  "objection",
  "however",
  "con",
  "against",
  "rebuttal",
  "reply",
  "although",
  "pero",
  "sin embargo",
  "objeción",
  "aber",
  "jedoch",
  "einwand",
  "mais",
  "cependant",
  "ma",
  "tuttavia",
  "obiezione",
  "mas",
  "porém",
  "objeção",
  "maar",
  "echter",
  "bezwaar",
  "ale",
  "jednak",
  "zarzut",
  "но",
  "однако",
  "возражение",
  "ama",
  "ancak",
  "[iİ]tiraz",
  "tetapi",
  "namun",
  "keberatan",
  "nhưng",
  "tuy nhiên",
  "phản bác",
  "लेकिन",
  "परंतु",
  "आपत्ति",
  "しかし",
  "反論",
  "하지만",
  "그러나",
  "반론",
  "但是",
  "但",
  "然而",
  "反驳",
  "لكن",
  "اعتراض",
].join("|");
const CLAIM_WORDS = [
  "main contention",
  "contention",
  "main claim",
  "claim",
  "conclusion",
  "thesis",
  "tesis",
  "afirmación",
  "conclusión",
  "these",
  "behauptung",
  "schlussfolgerung",
  "thèse",
  "affirmation",
  "tesi",
  "conclusione",
  "tese",
  "conclusão",
  "stelling",
  "bewering",
  "conclusie",
  "teza",
  "twierdzenie",
  "wniosek",
  "тезис",
  "утверждение",
  "вывод",
  "[iİ]ddia",
  "tez",
  "sonuç",
  "klaim",
  "kesimpulan",
  "luận điểm",
  "kết luận",
  "दावा",
  "निष्कर्ष",
  "主張",
  "結論",
  "주장",
  "결론",
  "论点",
  "論點",
  "主张",
  "结论",
  "أطروحة",
  "ادعاء",
  "استنتاج",
].join("|");

const SUPPORT_MARK = new RegExp(
  `^(?:\\+(?=\\s)|(?:${SUPPORT_WORDS})${COLON})\\s*`,
  "iu"
);
const OPPOSE_MARK = new RegExp(
  `^(?:[-−–—](?=\\s)|(?:${OPPOSE_WORDS})${COLON})\\s*`,
  "iu"
);
const CLAIM_PREFIX = new RegExp(`^(?:${CLAIM_WORDS})${COLON}\\s*`, "iu");

interface Read {
  text: string;
  stance: Stance | null;
}

function readLine(raw: string): Read {
  const trimmed = raw.trim();
  const support = SUPPORT_MARK.exec(trimmed);
  if (support) {
    return { text: trimmed.slice(support[0].length).trim(), stance: "support" };
  }
  const oppose = OPPOSE_MARK.exec(trimmed);
  if (oppose) {
    return { text: trimmed.slice(oppose[0].length).trim(), stance: "oppose" };
  }
  return { text: stripBullet(trimmed), stance: null };
}

function kindOf(
  stance: Stance | null,
  parent: ArgumentNode | null
): ArgumentKind {
  if (parent === null) {
    return "claim";
  }
  if (stance === "oppose") {
    return parent.kind === "objection" ? "rebuttal" : "objection";
  }
  return "reason";
}

/** Pop the stack back to the box a line of this indentation belongs under. */
function parentFor(
  stack: { id: number; indent: number }[],
  indent: number
): number | null {
  // The first line is the one main claim; it is never popped, so every other line,
  // however little it is indented, hangs somewhere beneath it.
  while (stack.length > 1 && (stack.at(-1)?.indent ?? 0) >= indent) {
    stack.pop();
  }
  return stack.at(-1)?.id ?? null;
}

function makeNode(
  nodes: ArgumentNode[],
  parent: ArgumentNode | null,
  read: Read,
  line: number
): ArgumentNode {
  const stance = parent === null ? null : (read.stance ?? "support");
  const position = parent === null ? 0 : parent.children.length;
  return {
    id: nodes.length,
    kind: kindOf(stance, parent),
    stance,
    text:
      parent === null ? read.text.replace(CLAIM_PREFIX, "").trim() : read.text,
    parent: parent?.id ?? null,
    children: [],
    depth: parent === null ? 0 : parent.depth + 1,
    line,
    number: parent === null ? "1" : `${parent.number}.${position + 1}`,
  };
}

/** Read the outline. Blank lines are skipped; lines beyond the limit are counted. */
export function parseArgumentMap(
  text: string,
  limit = ARGUMENT_LIMIT
): ArgumentParse {
  const nodes: ArgumentNode[] = [];
  const stack: { id: number; indent: number }[] = [];
  let dropped = 0;
  let unmarked = 0;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const read = readLine(raw);
    if (read.text === "") {
      continue;
    }
    if (nodes.length >= limit) {
      dropped += 1;
      continue;
    }
    const indent = indentOf(raw);
    const parentId = nodes.length === 0 ? null : parentFor(stack, indent);
    const parent = parentId === null ? null : nodes[parentId];
    if (parent !== null && read.stance === null) {
      unmarked += 1;
    }
    const node = makeNode(nodes, parent, read, index + 1);
    nodes.push(node);
    parent?.children.push(node.id);
    stack.push({ id: node.id, indent });
  }
  return { nodes, roots: nodes.length > 0 ? [0] : [], dropped, unmarked };
}

export function argumentCounts(
  parse: ArgumentParse
): Record<ArgumentKind, number> {
  const counts: Record<ArgumentKind, number> = {
    claim: 0,
    reason: 0,
    objection: 0,
    rebuttal: 0,
  };
  for (const node of parse.nodes) {
    counts[node.kind] += 1;
  }
  return counts;
}

/* ------------------------------------------------------------------ layout */

export type ArgumentDirection = "down" | "right";

export const BODY_FONT = 12.5;
export const TAG_FONT = 10.5;
const BODY_STYLE: TextStyle = { fontSize: BODY_FONT };
const MARGIN = 16;
const BOX_WIDTH = 196;
const PAD_X = 10;
const PAD_Y = 8;
const TAG_GAP = 3;
const SIBLING_GAP = 18;
const TREE_GAP = 40;
const LEVEL_GAP_DOWN = 58;
const LEVEL_GAP_RIGHT = 74;
const ATTACH_INSET = 14;
const MAX_LINES = 7;

export interface PlacedArgument {
  node: ArgumentNode;
  rect: Rect;
  lines: string[];
}

export interface ArgumentArrow {
  from: number;
  to: number;
  stance: Stance;
  points: [[number, number], [number, number]];
}

export interface ArgumentLayout {
  width: number;
  height: number;
  nodes: PlacedArgument[];
  arrows: ArgumentArrow[];
}

function bodyLines(node: ArgumentNode): string[] {
  return wrapText(node.text, BOX_WIDTH - PAD_X * 2, BODY_STYLE, MAX_LINES);
}

function boxHeight(lines: readonly string[]): number {
  return Math.ceil(
    TAG_FONT * LINE_HEIGHT +
      TAG_GAP +
      lines.length * BODY_FONT * LINE_HEIGHT +
      PAD_Y * 2
  );
}

function arrowFor(
  node: ArgumentNode,
  rect: Rect,
  parent: Rect,
  slot: number,
  down: boolean
): ArgumentArrow | null {
  if (node.parent === null || node.stance === null) {
    return null;
  }
  const points: ArgumentArrow["points"] = down
    ? [
        [rect.x + rect.width / 2, rect.y],
        [
          parent.x + ATTACH_INSET + (parent.width - 2 * ATTACH_INSET) * slot,
          parent.y + parent.height,
        ],
      ]
    : [
        [rect.x, rect.y + rect.height / 2],
        [
          parent.x + parent.width,
          parent.y + ATTACH_INSET + (parent.height - 2 * ATTACH_INSET) * slot,
        ],
      ];
  return { from: node.id, to: node.parent, stance: node.stance, points };
}

/** Lay the map out; `down` puts claims on top, `right` puts them at the left. */
export function layoutArgumentMap(
  parse: ArgumentParse,
  direction: ArgumentDirection
): ArgumentLayout {
  const lines = parse.nodes.map(bodyLines);
  const heights = lines.map(boxHeight);
  const down = direction === "down";
  const levels = Math.max(0, ...parse.nodes.map((node) => node.depth)) + 1;
  const levelHeight = Array.from({ length: levels }, () => 0);
  for (const node of parse.nodes) {
    levelHeight[node.depth] = Math.max(
      levelHeight[node.depth],
      heights[node.id]
    );
  }
  // Boxes of one level share a height when they stack down the page.
  const heightOf = (id: number) =>
    down ? levelHeight[parse.nodes[id].depth] : heights[id];
  const breadth = (id: number) => (down ? BOX_WIDTH : heightOf(id));
  const depthSize = (depth: number) => (down ? levelHeight[depth] : BOX_WIDTH);
  const gap = down ? LEVEL_GAP_DOWN : LEVEL_GAP_RIGHT;
  const offset: number[] = [];
  let cursor = MARGIN;
  for (let depth = 0; depth < levels; depth++) {
    offset.push(cursor);
    cursor += depthSize(depth) + gap;
  }

  const spans = new Map<number, number>();
  const spanOf = (id: number): number => {
    const known = spans.get(id);
    if (known !== undefined) {
      return known;
    }
    const kids = parse.nodes[id].children.map(spanOf);
    const own =
      kids.reduce((sum, kid) => sum + kid, 0) +
      SIBLING_GAP * Math.max(0, kids.length - 1);
    const span = Math.max(breadth(id), own);
    spans.set(id, span);
    return span;
  };

  const centres = new Map<number, number>();
  const place = (id: number, start: number): void => {
    const node = parse.nodes[id];
    const span = spanOf(id);
    if (node.children.length === 0) {
      centres.set(id, start + span / 2);
      return;
    }
    const total =
      node.children.reduce((sum, kid) => sum + spanOf(kid), 0) +
      SIBLING_GAP * (node.children.length - 1);
    let at = start + (span - total) / 2;
    for (const kid of node.children) {
      place(kid, at);
      at += spanOf(kid) + SIBLING_GAP;
    }
    const first = centres.get(node.children[0]) as number;
    const last = centres.get(node.children.at(-1) as number) as number;
    centres.set(id, (first + last) / 2);
  };

  let start = MARGIN;
  for (const root of parse.roots) {
    place(root, start);
    start += spanOf(root) + TREE_GAP;
  }
  const extent = Math.max(0, start - TREE_GAP) + MARGIN;

  const rects = new Map<number, Rect>();
  for (const node of parse.nodes) {
    const centre = centres.get(node.id) as number;
    const high = heightOf(node.id);
    rects.set(
      node.id,
      down
        ? {
            x: centre - BOX_WIDTH / 2,
            y: offset[node.depth],
            width: BOX_WIDTH,
            height: high,
          }
        : {
            x: offset[node.depth],
            y: centre - high / 2,
            width: BOX_WIDTH,
            height: high,
          }
    );
  }

  const arrows: ArgumentArrow[] = [];
  for (const node of parse.nodes) {
    if (node.parent === null) {
      continue;
    }
    const siblings = parse.nodes[node.parent].children;
    const slot = (siblings.indexOf(node.id) + 0.5) / siblings.length;
    const arrow = arrowFor(
      node,
      rects.get(node.id) as Rect,
      rects.get(node.parent) as Rect,
      slot,
      down
    );
    if (arrow) {
      arrows.push(arrow);
    }
  }

  const depthExtent = cursor - gap + MARGIN;
  return {
    width: Math.ceil(down ? extent : depthExtent),
    height: Math.ceil(down ? depthExtent : extent),
    nodes: parse.nodes.map((node) => ({
      node,
      rect: rects.get(node.id) as Rect,
      lines: lines[node.id],
    })),
    arrows,
  };
}

/** The widest line of a box, for the tests that check text fits its box. */
export function widestLine(lines: readonly string[]): number {
  return maxTextWidth(lines, BODY_STYLE);
}

/* ------------------------------------------------------- text alternative */

export interface ArgumentRow {
  number: string;
  kind: ArgumentKind;
  text: string;
  respondsTo: string;
}

export function argumentRows(parse: ArgumentParse): ArgumentRow[] {
  const rows: ArgumentRow[] = [];
  const walk = (id: number) => {
    const node = parse.nodes[id];
    rows.push({
      number: node.number,
      kind: node.kind,
      text: node.text,
      respondsTo: node.parent === null ? "" : parse.nodes[node.parent].number,
    });
    for (const child of node.children) {
      walk(child);
    }
  };
  for (const root of parse.roots) {
    walk(root);
  }
  return rows;
}

const MARKS: Record<ArgumentKind, string> = {
  claim: "",
  reason: "+ ",
  objection: "- ",
  rebuttal: "- ",
};

/** The map written back in the outline syntax, with every marker made explicit. */
export function argumentOutline(parse: ArgumentParse): string {
  const lines: string[] = [];
  const walk = (id: number) => {
    const node = parse.nodes[id];
    lines.push(`${"  ".repeat(node.depth)}${MARKS[node.kind]}${node.text}`);
    for (const child of node.children) {
      walk(child);
    }
  };
  for (const root of parse.roots) {
    walk(root);
  }
  return lines.join("\n");
}
